'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { XMarkIcon, PlusIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Field, Label } from '../../fieldsComponents/appointments/fieldset'

export default function DialogTimeEdit({ isOpen, onClose, onSave, initialTimeInputs }) {
  const [timeInputs, setTimeInputs] = useState([{ id: 1, start: '--:--', end: '--:--' }])

  useEffect(() => {
    if (isOpen) {
      if (initialTimeInputs && initialTimeInputs.length > 0) {
        setTimeInputs([...initialTimeInputs.map((input, index) => ({ id: index + 1, ...input })), { id: initialTimeInputs.length + 1, start: '--:--', end: '--:--' }])
      } else {
        setTimeInputs([{ id: 1, start: '--:--', end: '--:--' }])
      }
    }
  }, [isOpen, initialTimeInputs])

  const addTimeInput = () => {
    const lastInput = timeInputs[timeInputs.length - 1];
    if (lastInput.start !== '--:--' && lastInput.end !== '--:--') {
      setTimeInputs([...timeInputs, { id: timeInputs.length + 1, start: '--:--', end: '--:--' }])
    }
  }

  const removeTimeInput = (id) => {
    setTimeInputs(timeInputs.filter(input => input.id !== id))
  }

  const updateTimeInput = (id, field, value) => {
    setTimeInputs(timeInputs.map(input => input.id === id ? { ...input, [field]: value } : input))
  }

  const handleSave = () => {
    const validInputs = timeInputs.filter(input => input.start !== '--:--' && input.end !== '--:--');
    onSave(validInputs);
    onClose();
  }

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        times.push(time);
      }
    }
    return times;
  }

  const timeOptions = generateTimeOptions();

  const getFilteredStartTimeOptions = (previousEndTime) => {
    if (!previousEndTime || previousEndTime === '--:--') return timeOptions;
    const [endHour, endMinute] = previousEndTime.split(':').map(Number);
    return timeOptions.filter(time => {
      const [hour, minute] = time.split(':').map(Number);
      return hour > endHour || (hour === endHour && minute > endMinute);
    });
  };

  const getFilteredEndTimeOptions = (startTime) => {
    if (startTime === '--:--') return timeOptions;
    const [startHour, startMinute] = startTime.split(':').map(Number);
    return timeOptions.filter(time => {
      const [hour, minute] = time.split(':').map(Number);
      return hour > startHour || (hour === startHour && minute > startMinute);
    });
  };

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl sm:p-4"
          >
            <div className="relative p-0">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                aria-label="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            
            {/* Header with border-bottom */}
            <div className="border-b border-gray-200 py-4 mb-8">
              <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                Edit Time Slots
              </DialogTitle>
            </div>
            
            {/* Main content */}
            <div className="mt-8 px-4">
                {timeInputs.map((input, index) => (
                  <div key={input.id} className="flex space-x-4 items-center mb-4">
                    <Field className="flex-1">
                      <Label>Start Time</Label>
                      <Menu as="div" className="relative inline-block text-left w-full overflow-visible">
                        <div>
                          <MenuButton className="inline-flex w-full justify-between items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                            {input.start === '--:--' ? 'Select Time' : input.start}
                            <ChevronDownIcon aria-hidden="true" className="-mr-1 h-5 w-5 text-gray-400" />
                          </MenuButton>
                        </div>
                        <MenuItems
                          transition
                          className="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                        >
                          <div className="py-1 max-h-40 overflow-y-auto">
                            {getFilteredStartTimeOptions(index > 0 ? timeInputs[index - 1].end : null).map((time) => (
                              <MenuItem key={time}>
                                {({ active }) => (
                                  <button
                                    onClick={() => updateTimeInput(input.id, 'start', time)}
                                    className={`block w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}
                                  >
                                    {time}
                                  </button>
                                )}
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Menu>
                    </Field>
                    <Field className="flex-1">
                      <Label>End Time</Label>
                      <Menu as="div" className="relative inline-block text-left w-full overflow-visible">
                        <div>
                          <MenuButton className="inline-flex w-full justify-between items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                            {input.end === '--:--' ? 'Select Time' : input.end}
                            <ChevronDownIcon aria-hidden="true" className="-mr-1 h-5 w-5 text-gray-400" />
                          </MenuButton>
                        </div>
                        <MenuItems
                          transition
                          className="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                        >
                          <div className="py-1 max-h-40 overflow-y-auto">
                            {getFilteredEndTimeOptions(input.start).map((time) => (
                              <MenuItem key={time}>
                                {({ active }) => (
                                  <button
                                    onClick={() => updateTimeInput(input.id, 'end', time)}
                                    className={`block w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}
                                  >
                                    {time}
                                  </button>
                                )}
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Menu>
                    </Field>
                    {index === timeInputs.length - 1 ? (
                      <button
                        type="button"
                        onClick={addTimeInput}
                        className="rounded-md bg-black text-white font-bold p-2 mt-6 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeTimeInput(input.id)}
                        className="rounded-md bg-white text-red-600 p-2 mt-6 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer section with border */}
              <div className="border-t border-gray-200 mt-10">
                <div className="flex justify-between mt-5">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md bg-gray-200 text-gray-700 py-2 px-4 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-md bg-black text-white py-2 px-4 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                  >
                    Save
                  </button>
                </div>
              </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}